<template>
  <div class="bb-form-fieldset">
    <h3 class="bb-form-fieldset__header">{{ $t("settings.collaborators") }}</h3>

    <div v-if="loading">
      <LoadingButton />
    </div>

    <template v-else>
      <template v-if="collaborators.length > 0">
        <div class="bb-form-label">{{ $t("settings.activeCollaborators") }}</div>
        <div v-for="c in collaborators" :key="c.userId" class="colaborador">
          <div class="colaborador__left">
            <div class="colaborador__nombre">{{ c.name }}</div>
            <div class="colaborador__email">{{ c.email }}</div>
          </div>
          <button class="colaborador__delete" @click="removeCollaborator(c.userId)">
            <i class="uil uil-trash" v-tooltip="$t('settings.removeCollaborator')"></i>
          </button>
        </div>
      </template>

      <template v-if="invitations.length > 0">
        <div class="bb-form-label collaborator-manager__label--top">{{ $t("settings.pendingInvitations") }}</div>
        <div v-for="inv in invitations" :key="inv.id" class="colaborador">
          <div class="colaborador__left">
            <div class="colaborador__email">{{ inv.email }}</div>
          </div>
          <button class="colaborador__delete" @click="cancelInvitation(inv.id)">
            <i class="uil uil-trash" v-tooltip="$t('settings.cancelInvitation')"></i>
          </button>
        </div>
      </template>

      <div class="bb-form-item collaborator-manager__form">
        <input
          v-model="name"
          type="text"
          class="bb-form"
          :placeholder="$t('settings.inviteNamePlaceholder')"
          @keyup.enter="invite"
        />
        <input
          v-model="email"
          type="email"
          class="bb-form collaborator-manager__email"
          :placeholder="$t('settings.inviteEmailPlaceholder')"
          @input="emailError = null"
          @keyup.enter="invite"
        />
        <div class="bb-form-error" v-if="emailError">{{ emailError }}</div>
        <LoadingButton v-if="sending" />
        <button v-else class="button button--primary button--sm collaborator-manager__btn" @click="invite">
          {{ $t("settings.sendInvitation") }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { VTooltip } from "floating-vue";
import LoadingButton from "./LoadingButton.vue";

export default {
  name: "CollaboratorManager",
  components: { LoadingButton },
  directives: { tooltip: VTooltip },
  props: {
    listId: { type: [String, Number], required: true },
    legacyCollaborator: { type: Object, default: null },
    legacyInvitation: { type: String, default: null },
  },
  data() {
    return {
      collaborators: [],
      invitations: [],
      name: "",
      email: "",
      emailError: null,
      loading: true,
      sending: false,
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const [collaborators, invitations] = await Promise.all([
          this.$store.dispatch("fetchListCollaborators", this.listId),
          this.$store.dispatch("fetchListInvitations", this.listId),
        ]);
        this.collaborators = [
          ...(this.legacyCollaborator ? [{ ...this.legacyCollaborator, isLegacy: true }] : []),
          ...(collaborators || []),
        ];
        this.invitations = [
          ...(this.legacyInvitation ? [{ id: null, email: this.legacyInvitation, isLegacy: true }] : []),
          ...(invitations || []),
        ];
      } catch {
        // 403 — not owner, section will be hidden by parent
      } finally {
        this.loading = false;
      }
    },
    validateEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    async invite() {
      if (!this.email.trim()) return;
      if (!this.validateEmail(this.email)) {
        this.emailError = this.$t("generic.emailWrongFormat");
        return;
      }
      this.sending = true;
      const invitedName = this.name;
      const invitedEmail = this.email;
      try {
        await this.$store.dispatch("sendListInvitation", { listId: this.listId, email: invitedEmail, name: invitedName });
        window.mixpanel.track("Invitar col·laborador", {
          "Nombre del colaborador": invitedName,
          "Email del colaborador": invitedEmail,
        });
        this.name = "";
        this.email = "";
        this.$emit("success", this.$t("settings.invitationSent"));
        await this.fetchData();
      } catch (e) {
        if (e.response && e.response.status === 409) {
          this.emailError = this.$t("settings.invitationAlreadyExists");
        } else {
          this.$emit("error", this.$t("generic.errorNoSave"));
        }
      } finally {
        this.sending = false;
      }
    },
    async cancelInvitation(invitationId) {
      const inv = this.invitations.find(x => x.id === invitationId);
      try {
        if (inv?.isLegacy) {
          await this.$store.dispatch("deleteInvitation");
        } else {
          await this.$store.dispatch("cancelListInvitation", { listId: this.listId, invitationId });
        }
        window.mixpanel.track("Eliminar invitació", { "Invitation": inv?.email });
        this.$emit("success", this.$t("settings.invitationCancelled"));
        await this.fetchData();
      } catch {
        this.$emit("error", this.$t("generic.errorNoSave"));
      }
    },
    async removeCollaborator(collaboratorUserId) {
      const c = this.collaborators.find(x => x.userId === collaboratorUserId);
      try {
        if (c?.isLegacy) {
          await this.$store.dispatch("deleteCollaborator");
        } else {
          await this.$store.dispatch("removeListCollaborator", { listId: this.listId, collaboratorUserId });
        }
        window.mixpanel.track("Eliminar col·laborador", {
          "Nombre del colaborador": c?.name,
          "Email del colaborador": c?.email,
        });
        this.$emit("success", this.$t("settings.collaboratorRemoved"));
        await this.fetchData();
      } catch {
        this.$emit("error", this.$t("generic.errorNoSave"));
      }
    },
  },
};
</script>

<style scoped>
.collaborator-manager__label--top {
  margin-top: 1rem;
}
.collaborator-manager__form {
  margin-top: 1rem;
}
.collaborator-manager__email {
  margin-top: 0.5rem;
}
.collaborator-manager__btn {
  margin-top: 0.5rem;
}
</style>
